import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import hand from "../../../assets/waving-hand-sign_1f44b 1.png";
import v1 from "../../../assets/v1.png";
import { Link } from "react-router-dom";
import StudentDas1 from "../../../assets/StudentDas1.png";
import StudentDas2 from "../../../assets/StudentDas2.png";
import StudentDas3 from "../../../assets/StudentDas3.png";
import Banner from "../../../assets/new images/student banner.jpeg";
import axios from "axios";
import apiKey from "../../../config/apiKey";

const StudentMainBody = () => {
  const { t } = useTranslation();
  const student = JSON.parse(localStorage.getItem("edo-student"));
  const token = student?.token || "";
  const userId = student?.student?.id || "";

  const [loading, setLoading] = useState(true);
  const [formUser, setFormUser] = useState({ name: "" });
  const [dataTable, setDataTable] = useState([]);
  const [error, setError] = useState("");

  const enrolledSubjectsCount = useMemo(() => dataTable.length, [dataTable]);
  const uniqueTeacherCount = useMemo(() => {
    const ids = new Set();
    dataTable.forEach((n) => ids.add(n.teacher_id));
    return ids.size;
  }, [dataTable]);

  const lastThree = useMemo(() => (dataTable?.length ? dataTable.slice(-3) : []), [dataTable]);

  const fetchUser = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const res = await axios.get(`${apiKey}/api/edolutions/student/getuser/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormUser(res.data || {});
    } catch (err) {
      console.error("User fetch error:", err);
    }
  }, [userId, token]);

  const fetchNotifications = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${apiKey}/api/edolutions/student/read-notifications-all/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDataTable(res?.data?.data || []);
    } catch (err) {
      console.error("Notifications fetch error:", err);
      setError(t("studentDash.profile.loadError"));
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchUser();
    fetchNotifications();
  }, [fetchUser, fetchNotifications]);

  const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
  );

  return (
    <div className="w-full">
      {/* HERO — switched to your gradient */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-tr from-[#EE5F2B] to-[#2A3A54]">
        <div className="absolute -top-16 -right-16 h-64 w-64 rotate-12 rounded-3xl bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-12 h-56 w-56 -rotate-12 rounded-3xl bg-black/10 blur-2xl" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 md:p-8">
          <div className="lg:col-span-2 flex items-center gap-4">
      
            <div>
              <h1 className="text-white text-2xl md:text-3xl font-extrabold tracking-tight">
                {t("studentDash.profile.welcome")}{formUser?.name ? `, ${formUser.name}` : ""} 👋
              </h1>
              <p className="text-white/90 text-sm md:text-base">
                {t("studentDash.profile.subtitle")}
              </p>
            </div>
          </div>
          <div className="lg:col-span-1 flex items-center justify-center">
            <img
              src={Banner}
              alt=""
              className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow"
            />
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT COLUMN */}
        <div className="xl:col-span-2 space-y-6">
          {/* KPI cards — neutral base + subtle gradient chips */}
          <div className="no-scrollbar flex gap-4 overflow-x-auto pb-1">
            {loading ? (
              <>
                <Skeleton className="h-24 w-60" />
                <Skeleton className="h-24 w-60" />
                <Skeleton className="h-24 w-60" />
              </>
            ) : (
              <>
                <div className="min-w-[240px] flex-1 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <img src={StudentDas1} alt="" className="h-10 w-10" />
                    <div>
                      <p className="text-[11px] text-gray-500">{t("studentDash.profile.enrolledSessions")}</p>
                      <p className="text-2xl font-semibold text-gray-900">{enrolledSubjectsCount}</p>
                    </div>
                  </div>
                  <span className="mt-3 inline-block h-1 w-16 rounded-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" />
                </div>

                <div className="min-w-[240px] flex-1 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <img src={StudentDas2} alt="" className="h-10 w-10" />
                    <div>
                      <p className="text-[11px] text-gray-500">{t("studentDash.profile.totalMentors")}</p>
                      <p className="text-2xl font-semibold text-gray-900">{uniqueTeacherCount}</p>
                    </div>
                  </div>
                  <span className="mt-3 inline-block h-1 w-16 rounded-full bg-gradient-to-r from-[#2A3A54] to-[#EE5F2B]" />
                </div>

                <div className="min-w-[240px] flex-1 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <img src={StudentDas3} alt="" className="h-10 w-10" />
                    <div>
                      <p className="text-[11px] text-gray-500">{t("studentDash.profile.sessionsComplete")}</p>
                      <p className="text-2xl font-semibold text-gray-900">0</p>
                    </div>
                  </div>
                  <span className="mt-3 inline-block h-1 w-16 rounded-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" />
                </div>
              </>
            )}
          </div>

          {/* Recent teachers — chips + gradient accents */}
          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">{t("studentDash.profile.mySessions")}</h2>
              <Link
                to="/mentees/my-sessions"
                className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:opacity-90"
              >
                {t("studentDash.profile.viewAll")}
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <Skeleton className="h-28" />
                <Skeleton className="h-28" />
                <Skeleton className="h-28" />
              </div>
            ) : lastThree.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
                <p className="text-gray-800 font-semibold">{t("studentDash.profile.noMentorsTitle")}</p>
                <p className="text-gray-500 text-sm mt-1">
                  {t("studentDash.profile.noMentorsDesc")}
                </p>
                <Link
                  to="/mentees/my-sessions"
                  className="mt-4 inline-block rounded-lg px-4 py-2 text-white text-sm font-semibold hover:opacity-95 transition-colors bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                >
                  {t("studentDash.profile.findMentor")}
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {lastThree.map((item, idx) => (
                  <div
                    key={`${item.teacher_id}-${idx}`}
                    className="group relative rounded-2xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={v1}
                        alt=""
                        className="h-10 w-10 rounded-full ring-1 ring-gray-200 object-cover"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold capitalize text-gray-900">
                          {item.teacher_name}
                        </p>
                        <p className="truncate text-xs text-gray-500">{item.subjects}</p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700">
                        {t("studentDash.profile.classLabel", { class: item.class_name })}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700 capitalize">
                        {t("studentDash.profile.dayLabel", { day: item.day_name || t("studentDash.profile.undefined") })}
                      </span>
                      <span className="ml-auto inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] capitalize">
                        {item.type_name}
                      </span>
                    </div>

                    <div className="pointer-events-none absolute inset-x-6 -bottom-8 h-12 translate-y-3 bg-gradient-to-r from-[#EE5F2B]/10 to-[#2A3A54]/10 blur-xl transition-all group-hover:translate-y-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-6">
          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="text-base font-bold text-gray-900">{t("studentDash.profile.quickActions")}</h3>
            <div className="mt-4 space-y-3">
              <Link
                to="/mentees/my-sessions"
                className="block w-full rounded-xl px-4 py-2 text-center text-white text-sm font-semibold hover:opacity-95 transition-colors bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
              >
                {t("studentDash.profile.browseMentors")}
              </Link>
              <Link
                to="/mentees/my-sessions"
                className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2 text-center text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {t("studentDash.profile.viewMySessions")}
              </Link>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" />
              <h3 className="text-base font-bold text-gray-900">{t("studentDash.profile.tips")}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-gray-600 list-disc list-inside">
              <li>{t("studentDash.profile.tip1")}</li>
              <li>{t("studentDash.profile.tip2")}</li>
              <li>{t("studentDash.profile.tip3")}</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default StudentMainBody;
